import Image from 'next/image';
import Link from 'next/link';
import { useParams, usePathname } from 'next/navigation';
import type { FC } from 'react';
import { paths } from 'src/config/paths';
import { PRODUCT_ICONS } from 'src/modules/products/components/icons';
import { PRODUCT_LABELS, PRODUCTS } from 'src/modules/products/domain';
import { useProductStore } from 'src/modules/products/stores/productStore';

import { Tooltip } from '@/design-system/components/ui';
import { cn } from '@/design-system/lib/utils';

import { RailItem, sharedRailItems } from '../rail';

export const PRODUCT_RAIL_WIDTH = 56;

interface RailButtonProps {
  label: string;
  active?: boolean;
  children: React.ReactNode;
}

const railButtonClasses = (active?: boolean) =>
  cn(
    'tw:flex tw:h-10 tw:w-10 tw:items-center tw:justify-center tw:rounded-md tw:transition-colors',
    'tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-fuul-blue',
    active ? 'tw:bg-fuul-blue-alpha12 tw:text-fuul-blue' : 'tw:text-muted-foreground tw:hover:bg-fuul-blue-alpha4',
  );

const RailTooltip: FC<RailButtonProps> = ({ label, children }) => (
  <Tooltip content={label} side="right">
    {children}
  </Tooltip>
);

const SharedRailLink: FC<{ item: RailItem; active: boolean }> = ({ item, active }) => {
  const content = <span aria-hidden="true">{item.icon}</span>;

  return (
    <RailTooltip label={item.title}>
      {item.external ? (
        <a
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={item.title}
          className={railButtonClasses(false)}
        >
          {content}
        </a>
      ) : (
        <Link href={item.path} aria-label={item.title} className={railButtonClasses(active)}>
          {content}
        </Link>
      )}
    </RailTooltip>
  );
};

export const ProductRail: FC = () => {
  const params = useParams();
  const pathname = usePathname();
  const slug = params?.slug as string;
  const activeProduct = useProductStore((s) => s.activeProduct);
  const setActiveProduct = useProductStore((s) => s.setActiveProduct);

  return (
    <div
      className="tw:flex tw:flex-col tw:items-center tw:gap-1 tw:border-r tw:py-4"
      style={{ width: PRODUCT_RAIL_WIDTH, borderRightColor: 'var(--nav-border-color)' }}
    >
      <RailTooltip label="Go to overview">
        <Link
          href={slug ? paths.project.overview(slug) : '/'}
          aria-label="Go to overview"
          className="tw:mb-3 tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-md tw:hover:opacity-80"
        >
          <Image src="/assets/logos/fuul-isologo-color.webp" alt="Fuul" width={24} height={24} />
        </Link>
      </RailTooltip>

      <nav aria-label="Products" className="tw:flex tw:flex-col tw:items-center tw:gap-1">
        {PRODUCTS.map((product) => (
          <RailTooltip key={product} label={PRODUCT_LABELS[product]}>
            <button
              type="button"
              aria-label={PRODUCT_LABELS[product]}
              aria-current={product === activeProduct ? 'true' : undefined}
              onClick={() => setActiveProduct(product)}
              className={railButtonClasses(product === activeProduct)}
            >
              <span aria-hidden="true">{PRODUCT_ICONS[product]}</span>
            </button>
          </RailTooltip>
        ))}
      </nav>

      <div className="tw:mt-auto tw:flex tw:flex-col tw:items-center tw:gap-1">
        {sharedRailItems(slug).map((item) => (
          <SharedRailLink key={item.title} item={item} active={!item.external && pathname === item.path} />
        ))}
      </div>
    </div>
  );
};
